import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import NewNavbar from "../components/NewNavbar.js";
import GoogleMapsApiLoader from "../components/GoogleMapsApiLoader";
import { buildCartCheckoutPayload } from "../utils/cartCheckout";
import {
  selectCartItems,
  selectStandaloneCartItems,
  groupCartItems,
  isCollectionCartProduct,
} from "../store/cartSlice";
import { getProductPricing } from "../utils.js";
import { getCartBundlePricing } from "../utils/cartBundle.js";
import { logBeginCheckout } from "../utils/analytics";
import {
  usePostCartCheckoutMutation,
  useProfileReadQuery,
} from "../store/api.ts";
import Footer from "../components/Footer";
const CART_SHIPPING_ADDRESS_KEY = "cart_shipping_address";

const emptyShippingAddress = {
  streetAddress: "",
  apartment: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
};

const isValidAddressShape = (value) => {
  return (
    value &&
    typeof value === "object" &&
    "streetAddress" in value &&
    "apartment" in value &&
    "city" in value &&
    "state" in value &&
    "postalCode" in value &&
    "country" in value
  );
};

const getSavedCartAddress = () => {
  try {
    const raw = localStorage.getItem(CART_SHIPPING_ADDRESS_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return isValidAddressShape(parsed) ? parsed : null;
  } catch (error) {
    console.error("Failed to parse saved cart shipping address:", error);
    return null;
  }
};

const saveCartAddressToStorage = (address) => {
  try {
    localStorage.setItem(CART_SHIPPING_ADDRESS_KEY, JSON.stringify(address));
  } catch (error) {
    console.error("Failed to save cart shipping address:", error);
  }
};

const clearSavedCartAddress = () => {
  try {
    localStorage.removeItem(CART_SHIPPING_ADDRESS_KEY);
  } catch (error) {
    console.error("Failed to clear saved cart shipping address:", error);
  }
};

const buildFinalAddress = (values) => {
  const cityStateZip = [values.city, values.state, values.postalCode]
    .filter(Boolean)
    .join(", ")
    .replace(", ,", ",");

  return [values.streetAddress, values.apartment, cityStateZip, values.country]
    .filter((part) => part && part.trim())
    .join(", ");
};

const validateShippingAddress = (values) => {
  const errors = {};

  const city = values.city.trim();
  const state = values.state.trim();
  const postalCode = values.postalCode.trim();
  const country = values.country.trim();
  const streetAddress = values.streetAddress.trim();

  const hasLetter = /[A-Za-z]/;
  const hasDigit = /\d/;

  if (!streetAddress) errors.streetAddress = "Street Address is required.";

  if (!city) errors.city = "City is required.";
  else if (!hasLetter.test(city))
    errors.city = "City must contain letters, not just numbers.";

  if (!state) errors.state = "State is required.";
  else if (!hasLetter.test(state))
    errors.state = "State must contain letters, not just numbers.";

  if (!postalCode) errors.postalCode = "ZIP / Postal Code is required.";
  else if (!hasDigit.test(postalCode))
    errors.postalCode = "ZIP / Postal Code must contain at least one number.";

  if (!country) errors.country = "Country is required.";
  else if (!hasLetter.test(country))
    errors.country = "Country must contain letters, not just numbers.";

  return errors;
};

const ShippingAddressPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const allCartItems = useSelector(selectCartItems);
  const standaloneCartItems = useSelector(selectStandaloneCartItems);
  const requestedCheckoutMode = location.state?.checkoutMode;
  const checkoutMode =
      requestedCheckoutMode === "guest" ||
      requestedCheckoutMode === "single" ||
      requestedCheckoutMode === "bundle" ||
      requestedCheckoutMode === "cart"
        ? requestedCheckoutMode
        : "cart";

  const guestCheckoutData =
    checkoutMode === "guest"
      ? {
          email: location.state?.email || "",
          firstName: location.state?.delivery?.firstName || "",
          lastName: location.state?.delivery?.lastName || "",
          phone: location.state?.delivery?.phone || "",
        }
      : null;    
  const checkoutProductId = location.state?.checkoutProductId;

  const checkoutBundleId = location.state?.checkoutBundleId;
  const checkoutBundleInstanceId = location.state?.checkoutBundleInstanceId;
  const checkoutItems = useMemo(() => {
    if (checkoutMode === "single") {
      if (!checkoutProductId) {
        return [];
      }

      return standaloneCartItems.filter(
        (item) => {
          const variantId = Number(
            item?.product?.variant_id ??
              item?.product?.id
          );
          return (
            variantId ===
            Number(checkoutProductId)
          );
        },
      );
    }

    if (checkoutMode === "bundle") {
      const bundleLookupId =
        checkoutBundleInstanceId ||
        checkoutBundleId;

      if (!bundleLookupId) {
        return [];
      }

      return allCartItems.filter((item) => {
        const itemBundleLookupId = String(
          item?.bundle?.instanceId ||
          item?.bundle?.id ||
          ""
        );

        return (
          itemBundleLookupId ===
          String(bundleLookupId)
        );
      });
    }
    return allCartItems;
  }, [
    allCartItems,
    standaloneCartItems,
    checkoutMode,
    checkoutProductId,
    checkoutBundleId,
    checkoutBundleInstanceId,
  ]);

  const addressInputRef = useRef(null);
  const authToken = typeof window !== "undefined" ? localStorage.getItem("authToken") : null;
  const isAuthenticated = Boolean(authToken);

  const { data, error } = useProfileReadQuery( undefined, { skip: !isAuthenticated });
  const [createOrder, { isLoading: isCheckingOut }] =
    usePostCartCheckoutMutation();

  const [addressSearch, setAddressSearch] = useState(() => {
    const savedAddress = getSavedCartAddress();
    return savedAddress ? buildFinalAddress(savedAddress) : "";
  });

  const [shippingAddress, setShippingAddress] = useState(() => {
    if (checkoutMode === "guest" && location.state?.delivery) {
      const delivery = location.state.delivery;
      return {
        streetAddress: delivery.address1 || "",
        apartment: delivery.address2 || "",
        city: delivery.city || "",
        state: delivery.state || "",
        postalCode: delivery.postalCode || "",
        country: delivery.country || "",
      };
    }
    return getSavedCartAddress() || emptyShippingAddress;
  });

  const [addressErrors, setAddressErrors] = useState({});
  const [showAllAddressErrors, setShowAllAddressErrors] = useState(false);
  const [touchedFields, setTouchedFields] = useState({
    streetAddress: false,
    city: false,
    state: false,
    postalCode: false,
    country: false,
  });

  const orderTotals = useMemo(() => {
  const { bundles, standaloneItems } =
    groupCartItems(checkoutItems);

  const totals = {
    subtotal: 0,
    discount: 0,
    finalSubtotal: 0,
  };

  standaloneItems.forEach((item) => {
    const product = item?.product || {};
    const quantity = Number(
      item?.quantity || 1
    );

    const pricing = getProductPricing(
      product,
      isCollectionCartProduct(product),
    );

    const original = pricing.originalPrice * quantity;
    const final = pricing.discountedPrice * quantity;
    totals.subtotal += original;
    totals.discount += Math.max(original - final,  0);
    totals.finalSubtotal += final;
  });

  // Bundles
  bundles.forEach((bundle) => {
    const pricing = getCartBundlePricing(bundle);
    totals.subtotal += pricing.originalSubtotal;
    totals.discount += pricing.totalSavings;
    totals.finalSubtotal += pricing.total;
  });

  return totals;
  }, [checkoutItems]);

const subtotal = orderTotals.subtotal;
const discount = orderTotals.discount;
const total = orderTotals.finalSubtotal;

  const markFieldTouched = (field) => {
    setTouchedFields((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  const shouldShowFieldError = (field) => {
    return (
      (showAllAddressErrors || touchedFields[field]) && addressErrors[field]
    );
  };

  const handleShippingFieldChange = (field, value) => {
    setShippingAddress((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePlaceSelected = (parsedAddress, formattedAddress) => {
    const nextAddress = {
      ...emptyShippingAddress,
      ...parsedAddress,
    };

    setAddressSearch(formattedAddress || "");
    setShippingAddress(nextAddress);
    setAddressErrors(validateShippingAddress(nextAddress));
  };

  const beginCheckoutAnalyticsPayload = useMemo(() => {
    const items = checkoutItems.map((item, index) => {
    const product = item?.product || {};
    const quantity = Number(item?.quantity || 1);
    const price = Number(product?.price || 0);

    const productSlug =
      product?.real_slug ||
      product?.slug ||
      "";

    const productType =
      product?.productType ||
      product?.product_type ||
      (String(productSlug).startsWith("mrk-")
        ? "marketplace"
        : product?.is_marketplace === false
          ? "product"
          : "marketplace");

    return {
      item_id: String(product?.variant_id || product?.id || `cart-item-${index}`),
      item_name: product?.name || product?.product_name || "Product",
      price,
      quantity,
      currency: "USD",
      item_category: product?.garmentType || product?.category || "Fashion",
      item_variant: product?.variant_id
        ? String(product.variant_id)
        : product?.size
          ? String(product.size)
          : undefined,
      item_brand: product?.brand || undefined,
      item_list_name: "Cart",
      item_product_type: productType,
    };
  });

    const value = items.reduce((sum, item) => {
      return sum + Number(item.price || 0) * Number(item.quantity || 1);
    }, 0);

    return {
      value,
      items,
    };
  }, [checkoutItems]);

  const handleCheckout = async () => {
    const isGuestCheckout = checkoutMode === "guest";
    if (!isAuthenticated && !isGuestCheckout) {
      toast.error("Please sign in to checkout.");
      navigate("/cart", {
        replace: true,
        state: {
          authRequired: true,
          returnTo: "/shipping-address",
          },
        });
        return;
    }
    const errors = validateShippingAddress(shippingAddress);
    setShowAllAddressErrors(true);
    setAddressErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.error("Please complete your shipping address.");
      return;
    }

    if (!checkoutItems.length) {
      toast.error(
        checkoutMode === "single"
          ? "The selected product is no longer available in your cart."
          : "Your cart is empty.",
      );
      navigate("/cart");
      return;
    }

    const finalAddress = buildFinalAddress(shippingAddress);


    try {
      saveCartAddressToStorage(shippingAddress);
      logBeginCheckout(beginCheckoutAnalyticsPayload);

    const checkoutPayload = buildCartCheckoutPayload({
      cartItems: checkoutItems,
      address: finalAddress,
      customerFields:
      checkoutMode === "guest"? {
            email: guestCheckoutData.email.trim(),
            first_name: guestCheckoutData.firstName.trim(),
            last_name: guestCheckoutData.lastName.trim(),
            phone: guestCheckoutData.phone.trim(),
          }
        : {},
      });      
      if (checkoutMode === "guest") {
        if (!guestCheckoutData?.email?.trim() ||
          !guestCheckoutData?.firstName?.trim() ||
          !guestCheckoutData?.lastName?.trim() ||
          !guestCheckoutData?.phone?.trim()) {
          toast.error("Guest checkout information is incomplete.");
          navigate("/cart");
          return;
        }
      }

      const response = await createOrder({
        cart: checkoutPayload,
      }).unwrap();

      clearSavedCartAddress();

      if (response?.url) {
        window.location.href = response.url;
        return;
      }

      toast.error("Checkout URL not found.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong.");
    }
  };

  useEffect(() => {
    if (data) {
      const savedCartAddress = getSavedCartAddress();

      if (savedCartAddress) {
        setShippingAddress(savedCartAddress);
        setAddressSearch(buildFinalAddress(savedCartAddress));
        return;
      }

      const profileAddress = {
        streetAddress: data.address_line1?.trim() || "",
        apartment: data.address_line2?.trim() || "",
        city: data.city?.trim() || "",
        state: data.state?.trim() || "",
        postalCode: data.postal_code?.trim() || "",
        country: data.country?.trim() || "",
      };

      setShippingAddress(profileAddress);
      setAddressSearch(buildFinalAddress(profileAddress));
    }

    if (error) {
      console.error("Error fetching profile:", error);
    }
  }, [data, error]);

  useEffect(() => {
    const hasAnyTouchedField = Object.values(touchedFields).some(Boolean);

    if (showAllAddressErrors || hasAnyTouchedField) {
      setAddressErrors(validateShippingAddress(shippingAddress));
    }
  }, [shippingAddress, touchedFields, showAllAddressErrors]);

  return (
    <div className="min-h-screen bg-[#FAFAFB]">
      <NewNavbar bottomNav="cart" />
      <div className="mx-auto w-full max-w-[980px] px-4 pb-16 pt-10 sm:px-6 lg:px-8">
        <h1 className="text-[36px] font-bold leading-[34px] text-black">
          Shipping Information
        </h1>

        <p className="mt-3 text-[13px] text-[#8A8A95]">
          Confirm your shipping address before checkout.
        </p>

        <div className="mt-8 rounded-[14px] border border-[#E9E9EE] bg-white p-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.18em] text-[#6A7282] font-spaceMono">
                Search Address
              </label>

              <input
                ref={addressInputRef}
                type="text"
                value={addressSearch}
                onChange={(e) => setAddressSearch(e.target.value)}
                placeholder="Search address with Google Maps"
                className="h-[44px] w-full rounded-[6px] border border-[#DCDCE3] px-3 text-[14px] outline-none focus:border-[#17172A]"
              />

              <GoogleMapsApiLoader
                inputRef={addressInputRef}
                setAddressSearch={setAddressSearch}
                onPlaceSelected={handlePlaceSelected}
              />
            </div>

            <div>
              <label className="mb-1 block text-[10px] font-bold font-spaceMono text-[#6A7282] uppercase tracking-[0.18em] ">
                Address Line 1
              </label>
              <input
                value={shippingAddress.streetAddress}
                onChange={(e) =>
                  handleShippingFieldChange("streetAddress", e.target.value)
                }
                onBlur={() => markFieldTouched("streetAddress")}
                placeholder="Street address"
                className={`h-[44px] w-full rounded-[6px] border px-3 text-[14px] outline-none ${
                  shouldShowFieldError("streetAddress")
                    ? "border-red-500"
                    : "border-[#DCDCE3] focus:border-[#17172A]"
                }`}
              />
              {shouldShowFieldError("streetAddress") && (
                <p className="mt-1 text-[12px] text-red-500">
                  {addressErrors.streetAddress}
                </p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-[10px] font-bold font-spaceMono text-[#6A7282] uppercase tracking-[0.18em]">
                Address Line 2
              </label>
              <input
                value={shippingAddress.apartment}
                onChange={(e) =>
                  handleShippingFieldChange("apartment", e.target.value)
                }
                placeholder="Apartment, suite, unit, etc."
                className="h-[44px] w-full rounded-[6px] border border-[#DCDCE3] px-3 text-[14px] outline-none focus:border-[#17172A]"
              />
            </div>

            <div>
              <label className="mb-1 block text-[10px] font-bold font-spaceMono text-[#6A7282] uppercase tracking-[0.18em]">
                City
              </label>
              <input
                value={shippingAddress.city}
                onChange={(e) =>
                  handleShippingFieldChange("city", e.target.value)
                }
                onBlur={() => markFieldTouched("city")}
                placeholder="City"
                className={`h-[44px] w-full rounded-[6px] border px-3 text-[14px] outline-none ${
                  shouldShowFieldError("city")
                    ? "border-red-500"
                    : "border-[#DCDCE3] focus:border-[#17172A]"
                }`}
              />
              {shouldShowFieldError("city") && (
                <p className="mt-1 text-[12px] text-red-500">
                  {addressErrors.city}
                </p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-[10px] font-bold font-spaceMono text-[#6A7282] uppercase tracking-[0.18em]">
                State
              </label>
              <input
                value={shippingAddress.state}
                onChange={(e) =>
                  handleShippingFieldChange("state", e.target.value)
                }
                onBlur={() => markFieldTouched("state")}
                placeholder="State"
                className={`h-[44px] w-full rounded-[6px] border px-3 text-[14px] outline-none ${
                  shouldShowFieldError("state")
                    ? "border-red-500"
                    : "border-[#DCDCE3] focus:border-[#17172A]"
                }`}
              />
              {shouldShowFieldError("state") && (
                <p className="mt-1 text-[12px] text-red-500">
                  {addressErrors.state}
                </p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-[10px] font-bold font-spaceMono text-[#6A7282] uppercase tracking-[0.18em]">
                Country
              </label>
              <input
                value={shippingAddress.country}
                onChange={(e) =>
                  handleShippingFieldChange("country", e.target.value)
                }
                onBlur={() => markFieldTouched("country")}
                placeholder="Country"
                className={`h-[44px] w-full rounded-[6px] border px-3 text-[14px] outline-none ${
                  shouldShowFieldError("country")
                    ? "border-red-500"
                    : "border-[#DCDCE3] focus:border-[#17172A]"
                }`}
              />
              {shouldShowFieldError("country") && (
                <p className="mt-1 text-[12px] text-red-500">
                  {addressErrors.country}
                </p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-[10px] font-bold font-spaceMono text-[#6A7282] uppercase tracking-[0.18em]">
                Postal Code
              </label>
              <input
                value={shippingAddress.postalCode}
                onChange={(e) =>
                  handleShippingFieldChange("postalCode", e.target.value)
                }
                onBlur={() => markFieldTouched("postalCode")}
                placeholder="Postal code"
                className={`h-[44px] w-full rounded-[6px] border px-3 text-[14px] outline-none ${
                  shouldShowFieldError("postalCode")
                    ? "border-red-500"
                    : "border-[#DCDCE3] focus:border-[#17172A]"
                }`}
              />
              {shouldShowFieldError("postalCode") && (
                <p className="mt-1 text-[12px] text-red-500">
                  {addressErrors.postalCode}
                </p>
              )}
            </div>
          </div>

          <div className="mt-6 rounded-[8px] border border-[#E9E9EE] bg-[#FAFAFB] p-4">
            <div className="flex items-center justify-between text-[14px]">
              <span className="text-[#6F6F7B]">Subtotal</span>
              <span className="font-medium text-[#1C1C25]">
                ${subtotal.toFixed(2)}
              </span>
            </div>
            {/* Discount */}
            {discount > 0 ? (
              <div className="mt-3 flex items-center justify-between text-[14px]">
                <span className="text-[#6F6F7B]">
                  Discount
                </span>
                <span className="font-medium text-[#257A45]">
                  -${discount.toFixed(2)}
                </span>
              </div>
            ) : null}

            {/* Final amount */}
            <div className="mt-4 flex items-center justify-between border-t border-[#E9E9EE] pt-4 text-[14px]">
              <span className="font-semibold text-[#1C1C25]">
                Total
              </span>
              <span className="font-semibold text-[#1C1C25]">
                ${total.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => navigate("/cart")}
              className="inline-flex h-[44px] items-center justify-center rounded-[8px] bg-[#E0E0E5] px-8 text-[13px] font-medium text-[#6F6F7B]"
            >
              Back
            </button>

            <button
              type="button"
              onClick={handleCheckout}
              disabled={isCheckingOut || !checkoutItems.length}
              className="inline-flex h-[44px] items-center justify-center rounded-[8px] bg-[#17172A] px-8 text-[13px] font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isCheckingOut ? "Processing..." : "Proceed to Checkout"}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ShippingAddressPage;
